import { createApi } from '@reduxjs/toolkit/query/react';

// Mock data for dropdowns
const endpointsMockData = {
  region: [
    { label: 'Asia', value: 'asia' },
    { label: 'Europe', value: 'europe' },
    { label: 'North America', value: 'north_america' },
  ],
  country: {
    asia: [
      { label: 'India', value: 'india' },
      { label: 'Singapore', value: 'singapore' },
    ],
    europe: [
      { label: 'Germany', value: 'germany' },
      { label: 'France', value: 'france' },
    ],
    north_america: [
      { label: 'USA', value: 'usa' },
      { label: 'Canada', value: 'canada' },
    ],
  },
  reportType: [
    { label: 'Summary', value: 'summary' },
    { label: 'Detailed', value: 'detailed' },
    { label: 'Comparative', value: 'comparative' },
  ],
  grade: [
    { label: 'Executive', value: 'executive' },
    { label: 'Manager', value: 'manager' },
    { label: 'Senior Manager', value: 'senior_manager' },
  ],
  reportTitle: [
    { label: 'Executive', value: 'executive' },
    { label: 'Manager', value: 'manager' },
    { label: 'Senior Manager', value: 'senior_manager' },
  ],
  dataSource: [
    { label: 'Executive', value: 'executive' },
    { label: 'Manager', value: 'manager' },
    { label: 'Senior Manager', value: 'senior_manager' },
  ],
  sector: [
    { label: 'Tech', value: 'tech' },
    { label: 'Finance', value: 'finance' },
  ],
  companySize: [
    { label: 'Small', value: 'small' },
    { label: 'Medium', value: 'medium' },
    { label: 'Large', value: 'large' },
  ],
  industry: {
    tech: [
      { label: 'Manufacturing', value: 'manufacturing' },
      { label: 'Services', value: 'services' },
    ],
    finance: [
      { label: 'Banking', value: 'banking' },
      { label: 'Insurance', value: 'insurance' },
    ],
  },
  subIndustry: [
    { label: 'Automobile', value: 'automobile' },
    { label: 'IT Services', value: 'it_services' },
  ],
  rewardElement: [
    { label: 'Bonus', value: 'bonus' },
    { label: 'Stock', value: 'stock' },
    { label: 'Commission', value: 'commission' },
  ],
  showDataOn: [
    { label: '10th', value: '10' },
    { label: '50th', value: '50' },
    { label: '90th', value: '90' },
  ],
  ageTo: [
    { label: '2%', value: '2' },
    { label: '5%', value: '5' },
    { label: '10%', value: '10' },
  ],
};

// Mock query handler
const mockBaseQuery = async (args) => {
  const url = typeof args === 'string' ? args : args?.url;

  if (!url) {
    console.warn("⚠️ mockBaseQuery received invalid or undefined URL input:", args);
    return { data: { options: [] } };
  }

  console.log('🔍 mockBaseQuery URL:', url);
  const [endpoint, query] = url.split('?');
  const params = new URLSearchParams(query);
  const dependency = params.get('dependency');

  let data = [];

  try {
    if (dependency) {
      const nested = endpointsMockData[endpoint];
      data = nested?.[dependency] || [];
    } else {
      data = endpointsMockData[endpoint] || [];
    }

    console.log(`✅ Responding with ${data.length} items for '${endpoint}'${dependency ? ` (dependency: ${dependency})` : ''}`);
    return { data: { options: data } };
  } catch (error) {
    console.error('❌ Error in mockBaseQuery:', error);
    return { error: { status: 500, data: 'Internal Mock Error' } };
  }
};

// RTK Query API
export const dynamicReportsApi = createApi({
  reducerPath: 'dynamicReportApi',
  baseQuery: mockBaseQuery,
  endpoints: (builder) => ({
    getRegionOptions: builder.query({ query: () => 'region' }),
    getCountryOptions: builder.query({ query: (region) => `country?dependency=${region}` }),
    getReportTypeOptions: builder.query({ query: () => 'reportType' }),
    getGradeOptions: builder.query({ query: () => 'grade' }),
    getReportTitleOptions: builder.query({ query: () => 'reportTitle' }),
    getDataSourceOptions: builder.query({ query: () => 'dataSource' }),
    getSectorOptions: builder.query({ query: () => 'sector' }),
    getCompanySizeOptions: builder.query({ query: () => 'companySize' }),
    getIndustryOptions: builder.query({ query: (sector) => `industry?dependency=${sector}` }),
    getSubIndustryOptions: builder.query({ query: () => 'subIndustry' }),
    getRewardElementOptions: builder.query({ query: () => 'rewardElement' }),
    getShowDataOnOptions: builder.query({ query: () => 'showDataOn' }),
    getAgeToOptions: builder.query({ query: () => 'ageTo' }),
  }),
});

export const {
  useGetRegionOptionsQuery,
  useGetCountryOptionsQuery,
  useGetReportTypeOptionsQuery,
  useGetGradeOptionsQuery,
  useGetReportTitleOptionsQuery,
  useGetDataSourceOptionsQuery,
  useGetSectorOptionsQuery,
  useGetCompanySizeOptionsQuery,
  useGetIndustryOptionsQuery,
  useGetSubIndustryOptionsQuery,
  useGetRewardElementOptionsQuery,
  useGetShowDataOnOptionsQuery,
  useGetAgeToOptionsQuery,
} = dynamicReportsApi;